import React, { useEffect, useMemo, useRef, useState } from 'react';

// Use backend proxy so keys live in backend/.env
const BACKEND_BASE = (
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) ||
  (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    // ? 'http://localhost:8000'
    ? 'https://mhub.maskantech.in'
    : '')
);
const API_URL = BACKEND_BASE + '/api/openrouter/chat';
const MODEL_LABELS = {
  normal: 'google/gemini-2.0-flash-exp:free',
  negotiation: 'google/gemini-2.0-flash-exp:free',
  image: 'google/gemini-2.0-flash-exp:free',
  multimodal: 'google/gemini-2.0-flash-exp:free',
  planning: 'Auto (fallbacks)'
};

function classNames(...parts) {
  return parts.filter(Boolean).join(' ');
}

function LaunchArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 17L17 7" />
      <path d="M7 7h10v10" />
    </svg>
  );
}

function ChatHeader({ tab, setTab, isBusy }) {
  const cards = [
    { id: 'normal', title: 'Normal', sub: 'General assistant', icon: '👥' },
    { id: 'negotiation', title: 'Negotiation', sub: 'Deal & pricing coach', icon: '🤝' },
    { id: 'image', title: 'Image', sub: 'Visual analysis', icon: '🖼️' },
    { id: 'multimodal', title: 'Multimodal', sub: 'Text + images', icon: '✨' },
    { id: 'planning', title: 'Planning Report', sub: 'Qatar code guidance', icon: '🏗️' },
  ];
  return (
    <div className="chat-header">
      <div className="cards">
        {cards.map((c) => (
          <div
            key={c.id}
            className={classNames('card', tab === c.id && 'active')}
            onClick={() => !isBusy && setTab(c.id)}
            title={c.title}
          >
            <div className="ico" aria-hidden>{c.icon}</div>
            <div className="title">{c.title}</div>
            <div className="sub">{c.sub}</div>
            <div className="go" aria-hidden>
              <LaunchArrowIcon />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Bubble({ role, text, images }) {
  return (
    <div className={classNames('msg', role)}>
      <div className="bubble">
        {text && <div className="text" dangerouslySetInnerHTML={{ __html: escapeToHtml(text) }} />}
        {!!images?.length && (
          <div className="imgs">
            {images.map((src, i) => (
              <img src={src} alt={`upload-${i}`} key={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function escapeToHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br/>');
}

function useAutoScroll(dep) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) el.scrollTop = el.scrollHeight + 1000;
  }, [dep]);
  return ref;
}

// System prompts and model selection live in backend under backend/prompts

export default function Chatbot() {
  const [tab, setTab] = useState('normal');
  const [messages, setMessages] = useState({ normal: [], negotiation: [], image: [], multimodal: [], planning: [] });
  const [input, setInput] = useState('');
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const viewRef = useAutoScroll(messages[tab]);

  const placeholder = useMemo(() => {
    if (tab === 'negotiation') return 'Describe your position, constraints, or target terms...';
    if (tab === 'image') return 'Upload and ask about the plan image...';
    if (tab === 'multimodal') return 'Ask anything and optionally add images...';
    if (tab === 'planning') return 'Upload plan images (or export pages from PDF/DXF/IFC) and request a Qatar-compliant report...';
    return 'Ask anything...';
  }, [tab]);

  function pushMessage(kind, msg) {
    setMessages((m) => ({ ...m, [kind]: [...m[kind], msg] }));
  }

  function buildClientMessages(list) {
    // Keep a lean schema; backend adds system prompt and converts for OpenRouter
    return list.map((m) => ({ role: m.role, text: m.text || '', images: m.images || [] }));
  }

  async function callOpenRouter(kind, list) {
    const body = { kind, messages: buildClientMessages(list), stream: false };
    const res = await fetch(API_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || `HTTP ${res.status}`);
    }
    const data = await res.json();
    return String(data?.content || '');
  }

  async function onSend() {
    setError('');
    if (busy) return;
    const isVision = tab === 'image' || tab === 'multimodal' || tab === 'planning';
    if (!input && !isVision) return;

    const userMsg = { role: 'user', text: input.trim(), images: isVision ? images : [] };
    pushMessage(tab, userMsg);
    setInput('');
    if (isVision) setImages([]);
    setBusy(true);
    try {
      const list = [...messages[tab], userMsg];
      const reply = await callOpenRouter(tab, list);
      pushMessage(tab, { role: 'assistant', text: reply });
    } catch (e) {
      setError(typeof e?.message === 'string' ? e.message : 'Request failed');
    } finally {
      setBusy(false);
    }
  }

  function onKeyDown(e) {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') onSend();
  }

  function onClear() {
    setMessages((m) => ({ ...m, [tab]: [] }));
    setError('');
  }

  async function onPickFiles(ev) {
    const files = Array.from(ev.target.files || []);
    if (!files.length) return;
    const max = 4;
    const slice = files.slice(0, max);
    const loaded = await Promise.all(
      slice.map(
        (f) =>
          new Promise((resolve) => {
            const fr = new FileReader();
            fr.onload = () => resolve(String(fr.result));
            fr.readAsDataURL(f);
          })
      )
    );
    setImages((prev) => [...prev, ...loaded].slice(0, max));
    ev.target.value = '';
  }

  const active = messages[tab] || [];
  const modelName = MODEL_LABELS[tab] || 'auto';

  return (
    <div className="page">
      <header className="header"><h1>Chatbots</h1></header>
      <main className="container">
        <div className="panel chat-panel">
          <ChatHeader
            tab={tab}
            setTab={setTab}
            isBusy={busy}
          />
          <div className="subhead">
            <div className="muted">Model: {modelName}</div>
            {!!error && <div className="error">{error}</div>}
            <div style={{flex:1}} />
            <button className="clear-btn" onClick={onClear} disabled={busy}>Clear</button>
          </div>

          {(tab === 'image' || tab === 'multimodal' || tab === 'planning') && (
            <div className="uploader">
              <input type="file" accept="image/*" multiple onChange={onPickFiles} />
              {!!images.length && (
                <div className="thumbs">
                  {images.map((src, i) => (
                    <div className="th" key={i}>
                      <img src={src} alt={`preview-${i}`} />
                    </div>
                  ))}
                </div>
              )}
              {tab === 'planning' && (
                <div className="muted" style={{marginTop:6}}>
                  Tip: Export relevant pages from PDF/DXF/IFC as images (PNG/JPEG) and upload. Include scale or key dimensions in the prompt for better accuracy.
                </div>
              )}
            </div>
          )}

          <div className="chatbox">
            <div className="viewport" ref={viewRef}>
              {active.length === 0 && (
                <div className="empty">
                  {tab === 'normal' && (
                    <>
                      <div className="title">Normal Chatbot</div>
                      <div className="hint">Ask general questions. Use Ctrl/Cmd + Enter to send.</div>
                    </>
                  )}
                  {tab === 'negotiation' && (
                    <>
                      <div className="title">Negotiation Chatbot</div>
                      <div className="hint">Describe your goals, constraints, and target terms.</div>
                    </>
                  )}
                  {tab === 'image' && (
                    <>
                      <div className="title">Image Analysis</div>
                      <div className="hint">Upload images and ask what to analyze.</div>
                    </>
                  )}
                  {tab === 'multimodal' && (
                    <>
                      <div className="title">Multimodal Chatbot</div>
                      <div className="hint">Combine text and images for richer reasoning.</div>
                    </>
                  )}
                </div>
              )}
              {active.map((m, i) => (
                <Bubble key={i} role={m.role} text={m.text} images={m.images} />
              ))}
            </div>

            <div className="composer">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                disabled={busy}
              />
              <button onClick={onSend} disabled={busy}>
                {busy ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>

        <style>{`
          .chat-panel { padding: 0; overflow: hidden; }
          .chat-header { padding:16px; border-bottom:1px solid #e6eaf1; background: linear-gradient(180deg,#f7fbff,#ecf5ff); border-radius: 12px 12px 0 0; }
          .cards { display:grid; grid-template-columns: repeat(4, 1fr); gap:12px; }
          .card { position:relative; border-radius:16px; background: linear-gradient(180deg, #7aa2ff, #3f6cff); color:#fff; padding:16px; box-shadow:0 16px 32px rgba(63,108,255,.25); border:1px solid rgba(255,255,255,.18); cursor:pointer; transition: transform .08s ease, box-shadow .12s; }
          .card:hover { transform: translateY(-2px); box-shadow:0 18px 40px rgba(63,108,255,.35); }
          .card.active { outline: 2px solid rgba(255,255,255,.8); }
          .card .ico { width:36px; height:36px; display:flex; align-items:center; justify-content:center; background: rgba(255,255,255,.18); border-radius:10px; margin-bottom:24px; }
          .card .title { font-size:20px; font-weight:700; line-height:1; }
          .card .sub { font-size:12px; opacity:.9; margin-top:6px; text-transform: uppercase; letter-spacing: .06em; }
          .card .go { position:absolute; right:12px; top:12px; width:26px; height:26px; border-radius:999px; border:1px solid rgba(255,255,255,.7); display:flex; align-items:center; justify-content:center; opacity:.8; }
          .card .go svg { width:14px; height:14px; }
          .muted { color:#6b7a99; font-size:12px; }
          .warn { color:#b02a37; background:#fdecee; border:1px solid #f5c2c7; padding:6px 10px; border-radius:10px; }
          .error { color:#b02a37; background:#fdecee; border:1px solid #f5c2c7; padding:6px 10px; border-radius:10px; }
          .subhead { display:flex; align-items:center; gap:10px; padding: 10px 16px; }

          .uploader { padding: 0 16px 8px 16px; display:grid; gap:8px; }
          .thumbs { display:flex; gap:8px; flex-wrap: wrap; }
          .th { width:64px; height:64px; border-radius:10px; overflow:hidden; border:1px solid #e6eaf1; background:#fff; display:flex; align-items:center; justify-content:center; }
          .th img { width:100%; height:100%; object-fit:cover; display:block; }

          .chatbox { display:grid; grid-template-rows: 1fr auto; height: 64vh; }
          .viewport { overflow:auto; padding: 12px 16px; background:#fff; }
          .empty { text-align:center; color:#6b7a99; padding: 30px 0; }
          .empty .title { font-weight:700; color:#1e2a55; margin-bottom:6px; }
          .empty .hint { color:#6b7a99; }

          .msg { display:flex; margin: 8px 0; }
          .msg.user { justify-content:flex-end; }
          .bubble { max-width: 70%; padding: 12px 14px; border-radius: 16px; border: 1px solid #e6eaf1; background:#ffffff; color:#0b1220; box-shadow:0 6px 18px rgba(2,6,23,.06); }
          .msg.user .bubble { background: linear-gradient(180deg,#7aa2ff,#3f6cff); color:#fff; border-color: transparent; }
          .text { line-height:1.4; }
          .imgs { display:flex; gap:6px; margin-top:8px; }
          .imgs img { width:160px; height:120px; object-fit:cover; border-radius:10px; border:1px solid #e6eaf1; }

          .composer { display:flex; gap:8px; padding: 10px 16px 16px; border-top:1px solid #e6eaf1; background:#fff; }
          .composer input { flex:1; }
          .composer button { background: linear-gradient(180deg,#7aa2ff,#3f6cff); border: none; }
          .clear-btn { background:#f4f7ff; color:#1e2a55; border:1px solid #cdd8ff; height:auto; padding:8px 12px; border-radius:10px; cursor:pointer; }
          .clear-btn[disabled] { opacity:.6; cursor:not-allowed; }
        `}</style>
      </main>
    </div>
  );
}
